import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
    Calendar, 
    FileText, 
    Send, 
    Users, 
    Link2, 
    TrendingUp,
    Plus,
    Clock,
    ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

export default function Dashboard() {
    const { api, user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await api.get('/dashboard/stats');
                setStats(response.data);
            } catch (error) {
                console.error('Failed to fetch stats:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [api]);

    const statCards = [
        {
            title: 'Scheduled Posts',
            value: stats?.scheduled_posts || 0,
            icon: Calendar,
            color: 'text-cyan-500',
            bg: 'bg-cyan-500/10'
        },
        {
            title: 'Published',
            value: stats?.published_posts || 0,
            icon: Send,
            color: 'text-green-500',
            bg: 'bg-green-500/10'
        },
        {
            title: 'Drafts',
            value: stats?.draft_posts || 0,
            icon: FileText,
            color: 'text-yellow-500',
            bg: 'bg-yellow-500/10'
        },
        {
            title: 'Connected Accounts',
            value: stats?.connected_accounts || 0,
            icon: Link2,
            color: 'text-fuchsia-500',
            bg: 'bg-fuchsia-500/10'
        },
    ];

    const getPlatformIcon = (platform) => {
        const icons = {
            instagram: '📸',
            facebook: '👤',
            twitter: '🐦',
            linkedin: '💼',
            tiktok: '🎵',
            pinterest: '📌',
            youtube: '🎬'
        };
        return icons[platform] || '📱';
    };

    const getStatusBadge = (status) => {
        const styles = {
            draft: 'status-draft',
            scheduled: 'status-scheduled',
            published: 'status-published',
            failed: 'status-failed',
            pending_approval: 'status-pending'
        };
        return styles[status] || 'status-draft';
    };

    return (
        <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold">
                        Welcome back, {user?.name?.split(' ')[0]}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Here's what's happening with your social accounts
                    </p>
                </div>
                <Button 
                    className="gradient-bg"
                    onClick={() => navigate('/compose')}
                    data-testid="create-post-btn"
                >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Post
                </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {statCards.map((stat, index) => (
                    <Card 
                        key={stat.title}
                        className="hover-lift cursor-pointer"
                        style={{ animationDelay: `${index * 0.1}s` }}
                        data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
                    >
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">
                                        {stat.title}
                                    </p>
                                    <p className="text-3xl font-bold mt-1">
                                        {loading ? '-' : stat.value}
                                    </p>
                                </div>
                                <div className={`p-3 rounded-lg ${stat.bg}`}>
                                    <stat.icon className={`h-6 w-6 ${stat.color}`} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card data-testid="upcoming-posts-card">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                            <Clock className="h-5 w-5 text-cyan-500" />
                            Upcoming Posts
                        </CardTitle>
                        <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate('/calendar')}
                        >
                            View All
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="space-y-3">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="h-16 skeleton-shimmer rounded-lg" />
                                ))}
                            </div>
                        ) : stats?.upcoming_posts?.length > 0 ? (
                            <div className="space-y-3">
                                {stats.upcoming_posts.map((post) => (
                                    <div
                                        key={post.id}
                                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                                        onClick={() => navigate(`/compose?edit=${post.id}`)}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">
                                                {post.caption?.slice(0, 50) || 'No caption'}
                                                {post.caption?.length > 50 ? '...' : ''}
                                            </p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-xs text-muted-foreground">
                                                    {post.scheduled_at ? format(new Date(post.scheduled_at), 'MMM d, h:mm a') : 'Not scheduled'}
                                                </span>
                                                <div className="flex gap-1">
                                                    {post.platforms?.map((p) => (
                                                        <span key={p} className="text-xs">
                                                            {getPlatformIcon(p)}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <Badge className={getStatusBadge(post.status)}>
                                            {post.status}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                <p>No upcoming posts</p>
                                <Button 
                                    variant="link" 
                                    className="mt-2 text-primary"
                                    onClick={() => navigate('/compose')}
                                >
                                    Create your first post
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card data-testid="quick-actions-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-fuchsia-500" />
                            Quick Actions
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 gap-3">
                            <Button
                                variant="outline"
                                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-primary/10 hover:border-primary"
                                onClick={() => navigate('/compose')}
                                data-testid="quick-create-post"
                            >
                                <Plus className="h-6 w-6 text-cyan-500" />
                                <span>New Post</span>
                            </Button>
                            <Button
                                variant="outline"
                                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-primary/10 hover:border-primary"
                                onClick={() => navigate('/media')}
                                data-testid="quick-upload-media"
                            >
                                <FileText className="h-6 w-6 text-fuchsia-500" />
                                <span>Upload Media</span>
                            </Button>
                            <Button
                                variant="outline"
                                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-primary/10 hover:border-primary"
                                onClick={() => navigate('/accounts')}
                                data-testid="quick-connect-account"
                            >
                                <Link2 className="h-6 w-6 text-green-500" />
                                <span>Connect Account</span>
                            </Button>
                            <Button
                                variant="outline"
                                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-primary/10 hover:border-primary"
                                onClick={() => navigate('/analytics')}
                                data-testid="quick-view-analytics"
                            >
                                <TrendingUp className="h-6 w-6 text-yellow-500" />
                                <span>Analytics</span>
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
